/**
 * Workspace items dropped on the chat composer, as context chips.
 *
 * The drag is the workspace's own item drag (`useItemDrag`: grid, list and
 * sidebar tree), carried under the private `TREE_NODE_DRAG_MIME`. It never
 * carries OS files, so it can never reach the attachment route: those are a
 * separate payload type (`Files`), which react-dropzone handles on the same
 * root. Items become exactly what the `@` picker adds: a folder is a
 * `project`, a file is its `artifactType`.
 */

import type { ChatContext } from '../../../contexts/ChatContextProvider';
import type { DraggedItem } from '../../navigation/HierarchyTree.dnd';

const keyOf = (c: Pick<ChatContext, 'type' | 'id'>) => `${c.type}:${c.id}`;

/** A dragged item as a context, or null for a file with no type (an old tab's payload). */
export function draggedToContext(item: DraggedItem): ChatContext | null {
  if (item.kind === 'folder') return { type: 'project', id: item.id, name: item.name };
  return item.artifactType ? { type: item.artifactType, id: item.id, name: item.name } : null;
}

export interface DroppedContextsResult {
  /** The manual picks after the drop. */
  readonly next: readonly ChatContext[];
  readonly added: number;
  /** Already in context (the page's own entity included). */
  readonly duplicates: number;
  /** Turned away by the cap. */
  readonly overCap: number;
  /** Files whose type the payload did not carry. */
  readonly unreadable: number;
}

/**
 * Appends the dropped items to the manual picks: each one once, never one
 * already shown (`shown` is every chip, the page's auto context included), and
 * never past `max` manual picks, the same ceiling the `@` picker keeps.
 */
export function mergeDroppedContexts(
  manual: readonly ChatContext[],
  shown: readonly ChatContext[],
  dropped: readonly DraggedItem[],
  max: number,
): DroppedContextsResult {
  const seen = new Set(shown.map(keyOf));
  const next = [...manual];
  let duplicates = 0;
  let overCap = 0;
  let unreadable = 0;
  for (const item of dropped) {
    const context = draggedToContext(item);
    if (!context) {
      unreadable += 1;
    } else if (seen.has(keyOf(context))) {
      duplicates += 1;
    } else if (next.length >= max) {
      overCap += 1;
    } else {
      seen.add(keyOf(context));
      next.push(context);
    }
  }
  return { next, added: next.length - manual.length, duplicates, overCap, unreadable };
}
